import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  CharityProfile,
  CharityProfilesResponse,
  ApiCharityProfilesResponse,
  ToggleDefaultProfileResponse,
  ProfileFieldsResponse,
  ApiProfileFieldsResponse,
  CreateUpdateProfilePayload,
  CreateUpdateProfileResponse,
  CreateUpdateProfileApiResponse,
  AllProfileFieldsResponse,
  ApiAllProfileFieldsResponse
} from '../../models/charity/charity.model';
import { RestfulApiService } from '../restful-api.service';

// Re-export types for convenience
export type {
  CharityProfile,
  CharityProfilesResponse,
  ToggleDefaultProfileResponse,
  ProfileFieldsResponse,
  CreateUpdateProfilePayload,
  CreateUpdateProfileResponse,
  AllProfileFieldsResponse
};

@Injectable({
  providedIn: 'root',
})
export class CharityProfileService {
  constructor(private api: RestfulApiService) {}

  /**
   * Get all charity profiles for organizations
   * @returns Observable<CharityProfilesResponse>
   */
  getCharityProfiles(): Observable<CharityProfilesResponse> {
    return this.api.get(
      '/v1/profiles/get-all/ORGANISATION'
    ).pipe(
      map((apiResponse: any) => this.mapApiResponseToProfiles(apiResponse))
    );
  }

  /**
   * Toggle a charity profile as default
   * @param uuid - The UUID of the profile to set as default
   * @returns Observable<ToggleDefaultProfileResponse>
   */
  setDefaultProfile(uuid: string): Observable<ToggleDefaultProfileResponse> {
    return this.api.put(
      `/v1/profiles/toggle-as-default/ORGANISATION/${uuid}`
    ).pipe(
      map((apiResponse: any) => ({
        success: true,
        message: apiResponse.message || 'Default profile updated successfully'
      }))
    );
  }

  /**
   * Delete a charity profile
   * @param uuid - The UUID of the profile to delete
   * @returns Observable<ToggleDefaultProfileResponse>
   */
  deleteProfile(uuid: string): Observable<ToggleDefaultProfileResponse> {
    return this.api.delete(
      `/v1/profiles/delete/${uuid}`
    ).pipe(
      map((apiResponse: any) => ({
        success: true,
        message: apiResponse.message || 'Profile deleted successfully'
      }))
    );
  }

  /**
   * Get profile fields by profile ID
   * @param uuid - The UUID of the profile
   * @returns Observable<ProfileFieldsResponse>
   */
  getProfileFieldsById(uuid: string): Observable<ProfileFieldsResponse> {
    return this.api.get(
      `/v1/profiles/${uuid}`
    ).pipe(
      map((apiResponse: any) => ({
        success: true,
        data: apiResponse.response,
        message: 'Profile fields retrieved successfully'
      }))
    );
  }

  /**
   * Get all charity profile fields for organization
   * @returns Observable<AllProfileFieldsResponse>
   */
  getAllProfileFields(): Observable<AllProfileFieldsResponse> {
    return this.api.get(
      '/v1/profiles/fields/ORGANISATION'
    ).pipe(
      map((apiResponse: any) => ({
        success: true,
        data: apiResponse.response,
        message: 'All profile fields retrieved successfully'
      }))
    );
  }

  /**
   * Check if a profile name is already taken
   * @param name - The profile name to check
   * @returns Observable<boolean> - true if taken
   */
  checkProfileName(name: string): Observable<boolean> {
    return this.api.get(`/v1/profiles/check-name/${encodeURIComponent(name)}`).pipe(
      map((apiResponse: any) => !!apiResponse.response)
    );
  }

  /**
   * Create or update a charity profile
   * @param payload - The profile data to create or update
   * @returns Observable<CreateUpdateProfileResponse>
   */
  createOrUpdateProfile(payload: CreateUpdateProfilePayload): Observable<CreateUpdateProfileResponse> {
    return this.api.post(
      '/v1/profiles/create-update',
      payload
    ).pipe(
      map((apiResponse: any) => ({
        success: true,
        profileUuid: apiResponse.response,
        message: payload.uuid ? 'Profile updated successfully' : 'Profile created successfully'
      }))
    );
  }

  /**
   * Maps the API response to the CharityProfilesResponse format
   * @param apiResponse - The raw API response
   * @returns CharityProfilesResponse - The mapped response
   */
  private mapApiResponseToProfiles(apiResponse: ApiCharityProfilesResponse): CharityProfilesResponse {
    const profiles: CharityProfile[] = apiResponse.response.map(profile => ({
      uuid: profile.uuid,
      name: profile.name,
      numberOfEntities: profile.numberOfEntities,
      default: profile.default
    }));

    return {
      success: true,
      data: profiles,
      message: 'Charity profiles retrieved successfully',
    };
  }
}
